import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Stethoscope, GraduationCap, Languages as LangIcon, Award, Building2, HeartPulse, ShieldCheck, Star, Users, Microscope, Activity, Zap, Bug, TestTube, Scissors, AlertTriangle, Dog, Cat, Rabbit, Bone, Phone, MessageCircle, CalendarDays, ArrowRight, Hand, FileText, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { CONTACT, GOOGLE_RATING } from '../data/mock';
import { cdnImg } from '../utils/cdn';
import { trackCallClick, trackWhatsAppClick, trackBookButtonClick } from '../lib/analytics';

const ASSET = 'https://customer-assets.emergentagent.com/job_petcare-preview-1/artifacts';
const DR_PORTRAIT = `${ASSET}/jnhxjj9q_WhatsApp%20Image%202026-07-08%20at%2012.31.40.jpeg`;
const HERO_IMAGE     = 'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?auto=format&fit=crop&w=1200&q=80';
const SURGERY_IMAGE  = `${ASSET}/hwfhshdc_OT1.jpg`;
const PREVENTIVE_IMG = 'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?auto=format&fit=crop&w=1200&q=80';
const TECH_IMAGES = {
  surgical:   `${ASSET}/hwfhshdc_OT1.jpg`,
  xray:       `${ASSET}/pcluy5gs_XRay2.jpg`,
  ecg:        `${ASSET}/ii5ykzjn_OT2.jpg`,
  cbc:        `${ASSET}/f4y1dcaz_CBC2.jpg`,
  microscope: `${ASSET}/2xnqhx06_microscope.jpg`,
  bp:         'https://images.unsplash.com/photo-1513224502586-d1e602410265?auto=format&fit=crop&w=1000&q=80',
};

const TITLE = 'Veterinary Services in Noida | Vaccinations, Diagnostics, Surgery | Ichi & Ori';
const DESCRIPTION = 'Trusted veterinary care in Sector 90, Noida. Consultations, vaccinations, in-house diagnostics, surgery and preventive healthcare for dogs, cats and rabbits, led by an experienced veterinarian.';
const set = (sel, attr, val) => { const el = document.head.querySelector(sel); if (el) el.setAttribute(attr, val); };

// —— Content (priority-ordered, human voice, no em dashes) ——
const HIGHLIGHTS_PRIORITIZED = [
  { icon: Users,        title: 'Experienced Veterinary Team', body: 'Led by Dr Kaushal Singh Yadav, with trained staff who take the time to understand every pet and every family that walks in.' },
  { icon: Star,         title: `${GOOGLE_RATING?.count || 107}+ Google Reviews`, body: `Rated ${GOOGLE_RATING?.rating || 4.9}/5 by pet parents across Noida, most of whom now come back for every visit.` },
  { icon: Building2,    title: 'Clean, Comfortable Clinic',   body: 'A calm, well-lit space in Sector 90, kept genuinely clean between every consultation. Handling stays unhurried, even on busy days.' },
  { icon: Microscope,   title: 'Diagnostics In-House',         body: 'Digital X-Ray, CBC, ECG and BP monitoring on-site, so most cases get their answers during the same visit.' },
  { icon: HeartPulse,   title: 'Gentle Handling',              body: 'Anxious pets are given time to settle. Every treatment is explained to the family before we begin, no surprises.' },
  { icon: AlertTriangle,title: 'Same-Day Urgent Slots',        body: 'When your pet cannot wait, neither should the appointment. Emergency slots kept aside daily during clinic hours.' },
  { icon: ShieldCheck,  title: 'Honest Preventive Care',       body: 'Vaccination and wellness plans built around what your pet actually needs. No unnecessary procedures, ever.' },
];

const IN_HOUSE = [
  { title: 'Digital DR X-Ray',       body: 'High-resolution direct-radiography for fractures, chest and abdominal cases, reviewed on screen within minutes.' },
  { title: 'CBC Analyzer',           body: 'Complete blood counts processed at the clinic, so results can guide the same-visit plan.' },
  { title: 'ECG Monitor',            body: 'Cardiac tracing and vitals monitoring, used during anaesthesia workups and for critical cases.' },
  { title: 'Blood Pressure Monitor', body: 'Non-invasive BP for senior pets, cardiac patients and pets with kidney concerns.' },
  { title: 'Laboratory Microscope',  body: 'On-site cytology and parasitology for ear, skin and stool samples.' },
  { title: 'Surgical Suite',         body: 'A sterile, dedicated room with careful anaesthesia and current instrumentation, designed around patient safety.' },
];

const PARTNER_LAB = [
  { title: 'Biochemistry',   body: 'Full organ-function and metabolic panels, processed through accredited reference laboratories.' },
  { title: 'Urine Analysis', body: 'Detailed urinalysis for kidney, urinary and metabolic assessment.' },
  { title: 'Histopathology', body: 'Tissue analysis for tumour diagnosis and disease confirmation.' },
];

const SURGERIES = [
  { title: 'Spaying',                body: 'Planned ovariohysterectomy for female dogs and cats, with a pre-anaesthetic workup and a structured recovery.' },
  { title: 'Neutering',              body: 'Routine male castration with careful anaesthetic protocols and clear post-operative instructions.' },
  { title: 'Tumour Removal',         body: 'Surgical excision of skin masses and superficial tumours, with biopsy referral where indicated.' },
  { title: 'Emergency Surgery',      body: 'Urgent intervention when your pet cannot wait, subject to case assessment and stabilisation.' },
  { title: 'Wound Repair',           body: 'Cleaning, debridement and suturing of lacerations, bite wounds and traumatic injuries.' },
  { title: 'Aural Hematoma Surgery', body: 'Corrective procedure for ear-flap hematomas that recur or do not settle with medication.' },
];

// Preventive — Annual Check-ups first, plus expanded vaccine coverage as bullets
const PREVENTIVE = [
  {
    icon: Stethoscope,
    title: 'Annual Health Check-ups',
    body: 'A once-a-year, nose-to-tail examination is the single most useful thing you can do for a healthy pet. We catch things early, so small issues stay small.',
  },
  {
    icon: Dog,
    title: 'Dog Vaccinations',
    body: 'WSAVA-guided schedules for every life stage.',
    bullets: [
      'DHPPi-L, Anti-Rabies, Kennel Cough (Bordetella)',
      'Leptospirosis, Canine Coronavirus',
      'Canine Influenza and Lyme (where indicated)',
      'Puppy schedules and annual boosters',
    ],
  },
  {
    icon: Cat,
    title: 'Cat Vaccinations',
    body: 'Kitten and adult protocols, tailored to lifestyle and history.',
    bullets: [
      'FVRCP, Anti-Rabies',
      'FeLV (Feline Leukemia)',
      'Kitten vaccination schedules',
      'Annual boosters',
    ],
  },
  {
    icon: Rabbit,
    title: 'Rabbit Vaccinations',
    body: 'Vaccination advice built around your rabbit’s lifestyle, environment and regional disease risk.',
  },
  {
    icon: Bug,
    title: 'Deworming & Parasite Control',
    body: 'Age-appropriate deworming and tick, flea and mite protection for the season and environment your pet actually lives in.',
  },
  {
    icon: Bone,
    title: 'Nutrition Counselling',
    body: 'Honest food reviews and diet advice for weight, life-stage and medical conditions. No pushy brand recommendations.',
  },
];

const TRUST_STRIP = [
  { icon: Award,        label: 'Experienced Veterinarian' },
  { icon: Microscope,   label: 'In-House Diagnostics' },
  { icon: Star,         label: `${GOOGLE_RATING?.rating || 4.9} Google Rating` },
  { icon: FileText,     label: 'Transparent Treatment Plans' },
  { icon: Hand,         label: 'Gentle Handling' },
  { icon: MessageCircle,label: 'WhatsApp Support' },
];

// Conversational, concise FAQs
const FAQS = [
  { q: 'When should I take my pet to a veterinarian?', a: 'Bring your pet in whenever something feels off, less appetite, vomiting, breathing trouble, a limp, or unusual behaviour. Also for routine care like vaccinations and wellness exams. When in doubt, it is always better to ask us early than wait it out.' },
  { q: 'Do I need an appointment before visiting?', a: 'Yes, appointments help us give your pet undivided attention and keep waiting to a minimum. Emergencies are always seen on priority, and a quick call before you leave home makes it faster for everyone.' },
  { q: 'What animals do you treat?', a: 'We currently look after dogs, cats and rabbits, from consultations and vaccinations through to diagnostics and surgical care.' },
  { q: 'What should I bring for my pet’s first visit?', a: 'Any past medical records, vaccination history, current medications and any recent test reports, if you have them. If it is your first pet, no problem, we will build the file together.' },
  { q: 'When should puppies receive their first vaccination?', a: 'Most puppies start their vaccination schedule between 6 and 8 weeks of age, followed by boosters. We will set up a schedule that matches your puppy’s age, breed and lifestyle.' },
  { q: 'How often do adult dogs and cats need vaccinations?', a: 'It depends on age, lifestyle, past vaccinations and regional risk. Some pets do best with annual boosters, others with tailored schedules. We will walk you through what makes sense for yours.' },
  { q: 'What diagnostic services are available at Ichi & Ori?', a: 'In-clinic, we run Digital DR X-Ray, CBC, ECG, blood pressure monitoring and microscopy for skin, ear and stool samples. For cases that need more, we work with trusted partner laboratories for biochemistry, urine analysis and histopathology.' },
  { q: 'Why are blood tests important before surgery?', a: 'They tell us how the body is functioning before anaesthesia begins. A quick pre-anaesthetic panel makes surgery safer, and lets us adjust the plan if something looks off.' },
  { q: 'Is anaesthesia safe for pets?', a: 'Veterinary anaesthesia today is very safe when it is paired with a proper health assessment, careful monitoring and the right protocol for that specific pet. We take all three seriously.' },
  { q: 'How long does recovery take after surgery?', a: 'Most routine procedures recover in a few days to two weeks. We send every family home with clear post-op instructions, and we are just a call away if anything feels unusual.' },
  { q: 'What should I do if my pet has a medical emergency?', a: 'Call us straight away on +91 9560925999. Fast action often changes outcomes, and we can advise you on what to do on the way in.' },
  { q: 'How often should my pet have a routine health check-up?', a: 'A yearly nose-to-tail check works well for most healthy adult pets. Puppies, kittens, seniors and pets with ongoing conditions may need more frequent visits.' },
];

const VeterinaryPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const canonical = 'https://ichiandoripetcare.com/veterinary';
    const orig = {
      d: document.head.querySelector('meta[name="description"]')?.getAttribute('content'),
      c: document.head.querySelector('link[rel="canonical"]')?.getAttribute('href'),
    };
    set('meta[name="description"]', 'content', DESCRIPTION);
    set('link[rel="canonical"]', 'href', canonical);
    set('meta[property="og:title"]', 'content', TITLE);
    set('meta[property="og:description"]', 'content', DESCRIPTION);
    set('meta[property="og:url"]', 'content', canonical);
    return () => {
      if (orig.d != null) set('meta[name="description"]', 'content', orig.d);
      if (orig.c != null) set('link[rel="canonical"]', 'href', orig.c);
    };
  }, []);

  const wa = `https://wa.me/${CONTACT.phoneRaw.replace(/\D/g,'')}?text=${encodeURIComponent('Hi, I\'d like to book a veterinary consultation at Ichi & Ori Pet Care.')}`;

  const faqSchema = { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: FAQS.map(f => ({ '@type':'Question', name:f.q, acceptedAnswer:{ '@type':'Answer', text:f.a } })) };
  const vetSchema = { '@context':'https://schema.org','@type':'VeterinaryCare', name:'Ichi and Ori Pet Care, Veterinary Services', url:'https://ichiandoripetcare.com/veterinary', telephone:CONTACT.phoneRaw, priceRange:'₹₹', address:{ '@type':'PostalAddress', streetAddress:'G11, Tower C, Noida World One', addressLocality:'Sector 90, Noida', addressRegion:'Uttar Pradesh', postalCode:'201305', addressCountry:'IN' }, aggregateRating:{ '@type':'AggregateRating', ratingValue:String(GOOGLE_RATING?.rating||4.9), reviewCount:String(GOOGLE_RATING?.count||107), bestRating:'5' }, areaServed:{ '@type':'City', name:'Noida' }, employee:{ '@type':'Person', name:'Dr Kaushal Singh Yadav', jobTitle:'Veterinary Surgeon', hasCredential:['BVSc & AH','Essentials of Veterinary Physiotherapy'] } };

  return (
    <div data-testid="vet-page" className="bg-[#fdf7fb]">
      <Helmet>
        <title>{TITLE}</title>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(vetSchema)}</script>
      </Helmet>

      {/* 1 — HERO */}
      <section className="relative bg-gradient-to-br from-[#1a0b3d] via-[#3b1d76] to-[#5b3eb5] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-25 [background-image:radial-gradient(circle_at_30%_20%,#e870b6_0%,transparent_40%),radial-gradient(circle_at_80%_70%,#5b3eb5_0%,transparent_45%)]" />
        <div className="relative max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 py-16 sm:py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-16 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-2 sm:py-1.5 rounded-2xl sm:rounded-full bg-[#e870b6]/20 border border-[#e870b6]/40 text-[#ffc1e0] text-[11px] sm:text-xs font-bold tracking-[0.18em] mb-6"><Stethoscope className="w-3.5 h-3.5 flex-shrink-0" /><span className="text-center leading-[1.4]"><span className="block sm:inline">TRUSTED VETERINARY HOSPITAL</span><span className="hidden sm:inline"> · </span><span className="block sm:inline">SECTOR 90, NOIDA</span></span></span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.03] mb-6">Thoughtful Veterinary Care for Every Stage of Your Pet&apos;s Life.</h1>
            <p className="text-base sm:text-lg text-white/85 leading-relaxed mb-9 max-w-xl">Complete veterinary care including consultations, vaccinations, diagnostics, surgery and preventive healthcare for dogs, cats and rabbits in Noida, delivered with gentle handling and transparent treatment plans.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/book" onClick={() => trackBookButtonClick('vet_hero')}><Button className="w-full sm:w-auto bg-[#e870b6] hover:bg-[#d85ba0] text-white rounded-full px-7 py-6 text-base font-semibold shadow-xl"><CalendarDays className="w-5 h-5 mr-2" />Request Appointment</Button></Link>
              <a href={wa} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick('vet_hero')}><Button className="w-full sm:w-auto bg-[#25d366] hover:bg-[#1eb858] text-white rounded-full px-7 py-6 text-base font-semibold shadow-xl"><MessageCircle className="w-5 h-5 mr-2" />Chat on WhatsApp</Button></a>
              <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCallClick('vet_hero')}><Button variant="outline" className="w-full sm:w-auto rounded-full px-7 py-6 text-base font-semibold border-white/40 text-white hover:bg-white/10"><Phone className="w-5 h-5 mr-2" />{CONTACT.phone}</Button></a>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl border border-white/15 aspect-[4/5]">
              <img src={HERO_IMAGE} alt="Veterinary examination at Ichi and Ori Pet Care, Noida" loading="eager" fetchPriority="high" decoding="async" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-4 -left-4 hidden lg:block bg-white text-[#1a0b3d] rounded-2xl shadow-xl px-5 py-3 border border-[#f5d3e6]">
              <div className="flex items-center gap-2 text-sm font-bold"><Star className="w-4 h-4 text-[#e870b6] fill-[#e870b6]" />{GOOGLE_RATING?.rating || 4.9}/5 on Google</div>
              <p className="text-xs text-[#3b1d76]/80">{GOOGLE_RATING?.count || 107}+ pet parents</p>
            </div>
          </div>
        </div>
      </section>

      {/* 2 — DR PROFILE */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-[minmax(280px,420px)_1fr] gap-10 lg:gap-16 items-start">
          <div className="relative hidden lg:block">
            <div className="rounded-[28px] overflow-hidden shadow-xl bg-white border border-[#f5d3e6]">
              <img src={cdnImg(DR_PORTRAIT, { w: 840, h: 1040, fit: 'cover', a: 'center' })} alt="Dr Kaushal Singh Yadav, BVSc & AH, Veterinary Surgeon at Ichi and Ori Pet Care, Noida" loading="lazy" decoding="async" className="w-full h-auto object-cover" data-testid="vet-portrait" />
            </div>
            <div className="hidden lg:block absolute -bottom-5 -right-5 rounded-2xl bg-[#1a0b3d] text-white px-5 py-3 shadow-xl border border-[#5b3eb5]/40">
              <p className="text-[10px] tracking-[0.2em] text-[#ffc1e0] font-bold mb-0.5">MEET YOUR VET</p>
              <p className="text-sm font-semibold">Dr Kaushal Singh Yadav</p>
            </div>
          </div>

          <div>
            <span className="inline-block text-xs font-bold tracking-[0.24em] text-[#5b3eb5] mb-4">MEET YOUR VETERINARIAN</span>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#1a0b3d] leading-[1.02] mb-3">Dr Kaushal<br/>Singh Yadav</h2>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2">
              <span className="text-[#e870b6] font-bold text-lg">BVSc &amp; AH</span>
              <span className="text-[#3b1d76]/30 font-light text-lg">|</span>
              <span className="text-[#5b3eb5] font-semibold text-lg">Veterinary Surgeon</span>
            </div>
            <p className="text-[#3b1d76]/75 italic text-[15px] sm:text-base mb-8">Helping dogs, cats and rabbits live healthier lives.</p>

            <p className="text-[#3b1d76] leading-relaxed text-base sm:text-lg max-w-xl mb-8 lg:mb-10">Dr Kaushal treats every pet as family. His focus is preventive medicine, careful diagnostics and gentle handling, and he always talks families through the plan before starting any treatment. Honest recommendations, no unnecessary procedures.</p>

            {/* Mobile-only portrait, placed after the intro paragraph */}
            <div className="lg:hidden mb-10">
              <div className="rounded-[24px] overflow-hidden shadow-xl bg-white border border-[#f5d3e6]">
                <img src={cdnImg(DR_PORTRAIT, { w: 720, h: 900, fit: 'cover', a: 'center' })} alt="Dr Kaushal Singh Yadav, BVSc & AH, Veterinary Surgeon at Ichi and Ori Pet Care, Noida" loading="lazy" decoding="async" className="w-full h-auto object-cover" data-testid="vet-portrait-mobile" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 sm:gap-5 mb-10 max-w-2xl" data-testid="dr-stats">
              {[
                { n: '20,000+', l: 'Consultations' },
                { n: '5,000+',  l: 'Families Served' },
                { n: '100+',    l: 'Surgeries Performed' },
              ].map((s, i) => (
                <div key={i} className="text-left min-w-0">
                  <div className="text-[22px] sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-none tracking-tight whitespace-nowrap">{s.n}</div>
                  <div className="h-px bg-gradient-to-r from-[#e870b6] to-transparent my-2.5 sm:my-3 w-10 sm:w-16" />
                  <div className="text-[11px] sm:text-sm font-semibold text-[#5b3eb5] tracking-wide leading-snug">{s.l}</div>
                </div>
              ))}
            </div>

            <div className="space-y-4 max-w-xl">
              <div className="flex items-start gap-4 pb-4 border-b border-[#f5d3e6]">
                <Award className="w-5 h-5 text-[#e870b6] mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-[11px] tracking-[0.2em] font-bold text-[#5b3eb5] mb-1">SPECIAL INTERESTS</div>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 rounded-full bg-[#fdf0f8] text-[#5b3eb5] text-sm font-semibold border border-[#f5d3e6]">Dermatology</span>
                    <span className="px-3 py-1 rounded-full bg-[#fdf0f8] text-[#5b3eb5] text-sm font-semibold border border-[#f5d3e6]">Internal Medicine</span>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4 pb-4 border-b border-[#f5d3e6]">
                <GraduationCap className="w-5 h-5 text-[#e870b6] mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-[11px] tracking-[0.2em] font-bold text-[#5b3eb5] mb-1">CERTIFICATION</div>
                  <p className="text-[#1a0b3d] text-sm">Essentials of Veterinary Physiotherapy</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <LangIcon className="w-5 h-5 text-[#e870b6] mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-[11px] tracking-[0.2em] font-bold text-[#5b3eb5] mb-1">LANGUAGES</div>
                  <p className="text-[#1a0b3d] text-sm">English, Hindi</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2.5 — LOCAL TRUST */}
      <section className="bg-white pt-4 pb-16 sm:pb-20">
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 lg:px-10 text-center">
          <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#e870b6] mb-4">SERVING NOIDA</span>
          <h2 className="text-2xl sm:text-3xl lg:text-[38px] font-extrabold text-[#1a0b3d] leading-[1.15] mb-5">Trusted by Pet Parents Across Noida.</h2>
          <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed max-w-[720px] mx-auto">
            Families from Sector 90, Sector 93, Sector 104, Sector 137, Sector 143, Sector 168 and nearby communities trust Ichi &amp; Ori for thoughtful veterinary care, grooming and preventive healthcare.
          </p>
          <div className="mt-8 flex items-center justify-center">
            <span className="h-px w-20 bg-gradient-to-r from-transparent via-[#e870b6]/60 to-transparent" />
          </div>
        </div>
      </section>

      {/* 3 — WHY CHOOSE (Built on Trust) */}
      <section className="bg-[#fdf7fb] py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="max-w-2xl mb-12">
            <span className="inline-block text-xs font-bold tracking-[0.24em] text-[#5b3eb5] mb-4">WHY PET PARENTS CHOOSE US</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.05]">Built on Trust,<br className="hidden sm:block"/> Designed Around Your Pet.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 sm:gap-5">
            <div className="md:col-span-4 rounded-[28px] p-8 sm:p-10 bg-gradient-to-br from-[#1a0b3d] to-[#5b3eb5] text-white shadow-lg relative overflow-hidden">
              <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#e870b6]/20 blur-3xl" />
              <div className="relative">
                <div className="w-14 h-14 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center mb-6"><Users className="w-7 h-7 text-[#ffc1e0]" /></div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-3">{HIGHLIGHTS_PRIORITIZED[0].title}</h3>
                <p className="text-white/85 text-base leading-relaxed max-w-md">{HIGHLIGHTS_PRIORITIZED[0].body}</p>
              </div>
            </div>
            <div className="md:col-span-2 rounded-[28px] p-8 bg-white border border-[#f5d3e6] shadow-sm flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-1 mb-4 text-[#e870b6]">
                  {[0,1,2,3,4].map(i => <Star key={i} className="w-5 h-5 fill-[#e870b6]" />)}
                </div>
                <div className="text-3xl sm:text-[34px] font-extrabold text-[#1a0b3d] leading-tight tracking-tight mb-2">{GOOGLE_RATING?.rating || 4.9}<span className="text-[#e870b6]">★</span> Google Rating</div>
                <p className="text-[15px] font-semibold text-[#1a0b3d]/85 leading-snug">Based on {GOOGLE_RATING?.count || 107}+ Verified Google Reviews</p>
              </div>
              <p className="text-[11px] font-bold tracking-[0.2em] text-[#5b3eb5] mt-4">FROM PET PARENTS ACROSS NOIDA</p>
            </div>

            {HIGHLIGHTS_PRIORITIZED.slice(2, 5).map((h, i) => (
              <div key={i} className="md:col-span-2 rounded-[24px] p-6 sm:p-7 bg-white border border-[#f5d3e6] hover:shadow-md transition-shadow">
                <div className="w-11 h-11 rounded-xl bg-[#5b3eb5]/10 flex items-center justify-center mb-4"><h.icon className="w-5 h-5 text-[#5b3eb5]" /></div>
                <h3 className="font-bold text-[#1a0b3d] text-lg mb-2">{h.title}</h3>
                <p className="text-sm text-[#3b1d76]/80 leading-relaxed">{h.body}</p>
              </div>
            ))}

            <div className="md:col-span-3 rounded-[24px] p-6 sm:p-7 bg-gradient-to-br from-[#fdf0f8] to-white border border-[#f5d3e6] flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-[#e870b6]/15 flex items-center justify-center flex-shrink-0"><AlertTriangle className="w-5 h-5 text-[#e870b6]" /></div>
              <div>
                <h3 className="font-bold text-[#1a0b3d] text-lg mb-2">{HIGHLIGHTS_PRIORITIZED[5].title}</h3>
                <p className="text-sm text-[#3b1d76]/80 leading-relaxed">{HIGHLIGHTS_PRIORITIZED[5].body}</p>
              </div>
            </div>
            <div className="md:col-span-3 rounded-[24px] p-6 sm:p-7 bg-gradient-to-br from-[#5b3eb5]/10 to-white border border-[#5b3eb5]/15 flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-[#5b3eb5]/15 flex items-center justify-center flex-shrink-0"><ShieldCheck className="w-5 h-5 text-[#5b3eb5]" /></div>
              <div>
                <h3 className="font-bold text-[#1a0b3d] text-lg mb-2">{HIGHLIGHTS_PRIORITIZED[6].title}</h3>
                <p className="text-sm text-[#3b1d76]/80 leading-relaxed">{HIGHLIGHTS_PRIORITIZED[6].body}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3.5 — TRUST INDICATORS ROW (below Why Choose) */}
      <section className="bg-[#fdf7fb] pt-2 pb-16 sm:pb-20">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 sm:gap-x-8 opacity-90">
            {[
              'Gentle Handling',
              'Transparent Treatment Plans',
              'Same-Day Diagnostics',
              'WhatsApp Follow-up',
              'Experienced Veterinary Team',
              'Honest Recommendations',
            ].map((t, i) => (
              <span key={i} className="inline-flex items-center gap-2 text-[13px] sm:text-sm text-[#1a0b3d]/85 font-medium">
                <span className="w-4 h-4 rounded-full bg-[#e870b6]/15 flex items-center justify-center text-[#e870b6] font-bold text-[10px]">✓</span>
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 4 — DIAGNOSTICS (Answers, Not Assumptions.) */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-10 lg:gap-16 items-start mb-20">
            <div className="lg:sticky lg:top-24">
              <span className="inline-block text-xs font-bold tracking-[0.24em] text-[#5b3eb5] mb-4">IN-HOUSE DIAGNOSTICS</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.05] mb-5">Answers,<br/>Not Assumptions.</h2>
              <p className="text-[#3b1d76]/85 text-base sm:text-lg leading-relaxed max-w-md mb-6">Most of what we need to diagnose your pet can be tested inside the clinic, so treatment usually begins on the same visit, without unnecessary delays.</p>
              <p className="text-[#1a0b3d] font-semibold text-base sm:text-lg leading-relaxed max-w-md mb-6">We believe treatment decisions should be guided by evidence, not guesswork.</p>
              <div className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.18em] text-[#e870b6]">
                <span className="w-8 h-px bg-[#e870b6]" />
                <span>SIX CORE CAPABILITIES</span>
              </div>
            </div>

            <ol className="space-y-0 border-t border-[#f5d3e6]">
              {IN_HOUSE.map((d, i) => (
                <li key={i} className="group grid grid-cols-[auto_1fr] gap-5 sm:gap-8 py-6 sm:py-7 border-b border-[#f5d3e6]">
                  <div className="text-[#e870b6] font-mono text-sm sm:text-base font-bold pt-1 tracking-widest">{String(i + 1).padStart(2, '0')}</div>
                  <div>
                    <h3 className="text-[#1a0b3d] font-bold text-xl sm:text-2xl mb-2 group-hover:text-[#5b3eb5] transition-colors">{d.title}</h3>
                    <p className="text-[#3b1d76]/80 leading-relaxed text-sm sm:text-base max-w-xl">{d.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="rounded-[28px] bg-gradient-to-br from-[#fdf0f8] via-[#fff8fb] to-white border border-[#f5d3e6] p-8 sm:p-12">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
              <div>
                <span className="inline-block text-xs font-bold tracking-[0.24em] text-[#e870b6] mb-3">PARTNER LABORATORY</span>
                <h3 className="text-2xl sm:text-3xl font-bold text-[#1a0b3d]">For Cases That Need<br className="hidden sm:block"/> A Closer Look.</h3>
              </div>
              <p className="text-sm text-[#3b1d76]/70 max-w-sm">Some investigations belong in a full reference laboratory. For those, we work with accredited partners we trust.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0 md:divide-x md:divide-[#f5d3e6]">
              {PARTNER_LAB.map((d, i) => (
                <div key={i} className={`py-5 md:py-2 md:px-6 ${i === 0 ? 'md:pl-0' : ''} ${i === PARTNER_LAB.length - 1 ? 'md:pr-0' : ''} border-b md:border-b-0 border-[#f5d3e6]`}>
                  <h4 className="text-[#1a0b3d] font-bold text-lg mb-2 flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#e870b6]" />{d.title}</h4>
                  <p className="text-sm text-[#3b1d76]/80 leading-relaxed">{d.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 5 — SURGERY */}
      <section className="bg-[#1a0b3d] text-white py-20 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(circle_at_15%_15%,#5b3eb5_0%,transparent_45%),radial-gradient(circle_at_85%_85%,#e870b6_0%,transparent_45%)]" />
        <div className="relative max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-10 lg:gap-16 items-start">
          <div className="lg:sticky lg:top-24">
            <span className="inline-block text-xs font-bold tracking-[0.24em] text-[#ffc1e0] mb-4">SURGERY</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.05] mb-5">Precise Procedures.<br/>Calm Recoveries.</h2>
            <p className="text-white/80 text-base sm:text-lg leading-relaxed mb-4 max-w-md">From planned procedures to emergencies, supported by careful anaesthesia protocols and structured post-operative care. Every family is walked through the plan, honestly, before we begin.</p>
            <p className="text-white/75 text-base sm:text-lg leading-relaxed mb-8 max-w-md">We&apos;ll explain the diagnosis, treatment options, expected recovery and estimated costs before proceeding, so you can make informed decisions with confidence.</p>
            <div className="rounded-2xl overflow-hidden border border-white/15 shadow-2xl max-w-md aspect-[5/4]">
              <img src={SURGERY_IMAGE} alt="Surgical suite at Ichi and Ori Pet Care, Noida" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>
          </div>

          <div className="divide-y divide-white/10">
            {SURGERIES.map((s, i) => (
              <div key={i} className="grid grid-cols-[auto_1fr] gap-5 sm:gap-8 py-6 sm:py-7 first:pt-0">
                <div className="w-10 h-10 rounded-full bg-[#e870b6]/15 border border-[#e870b6]/30 flex items-center justify-center flex-shrink-0"><Scissors className="w-4 h-4 text-[#ffc1e0]" /></div>
                <div>
                  <h3 className="font-bold text-xl sm:text-2xl text-white mb-2">{s.title}</h3>
                  <p className="text-white/75 leading-relaxed text-sm sm:text-base max-w-xl">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6 — PREVENTIVE (Prevention is the Best Medicine.) */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="max-w-2xl mb-12">
            <span className="inline-block text-xs font-bold tracking-[0.24em] text-[#5b3eb5] mb-4">STAY AHEAD</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.05] mb-4">Prevention is the Best Medicine.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed">The visits that never turn into emergencies start here. Every schedule is tailored to your pet&apos;s age, breed, lifestyle, medical history and our veterinarian&apos;s recommendations.</p>
          </div>

          {/* Featured — Annual Check-ups */}
          <div className="rounded-[28px] overflow-hidden bg-gradient-to-br from-[#fdf0f8] to-white border border-[#f5d3e6] grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] mb-6">
            <div className="p-8 sm:p-12 lg:p-14 flex flex-col justify-center">
              <div className="w-12 h-12 rounded-2xl bg-[#e870b6]/15 flex items-center justify-center mb-5"><Stethoscope className="w-6 h-6 text-[#e870b6]" /></div>
              <div className="text-[11px] tracking-[0.24em] font-bold text-[#e870b6] mb-3">FEATURED</div>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1a0b3d] leading-[1.1] mb-4">{PREVENTIVE[0].title}</h3>
              <p className="text-[#3b1d76]/85 leading-relaxed text-base sm:text-lg max-w-md">{PREVENTIVE[0].body}</p>
            </div>
            <div className="min-h-[240px] lg:min-h-full bg-gradient-to-br from-[#5b3eb5] to-[#1a0b3d] relative overflow-hidden">
              <img src={PREVENTIVE_IMG} alt="Annual pet health check-up at Ichi and Ori Pet Care, Noida" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-90" />
            </div>
          </div>

          {/* Remaining preventive items */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-0 border-t border-[#f5d3e6]">
            {PREVENTIVE.slice(1).map((p, i) => (
              <div key={i} className={`p-6 lg:p-7 border-b sm:border-b lg:border-b-0 border-[#f5d3e6] ${i < 4 ? 'sm:border-r lg:border-r' : ''} ${i === 1 ? 'sm:border-r-0 lg:border-r' : ''}`}>
                <div className="w-10 h-10 rounded-xl bg-[#5b3eb5]/10 flex items-center justify-center mb-4"><p.icon className="w-5 h-5 text-[#5b3eb5]" /></div>
                <h3 className="font-bold text-[#1a0b3d] text-base mb-2">{p.title}</h3>
                {p.body && <p className="text-xs text-[#3b1d76]/75 leading-relaxed mb-2">{p.body}</p>}
                {p.bullets && (
                  <ul className="space-y-1.5 mt-1">
                    {p.bullets.map((b, j) => (
                      <li key={j} className="text-xs text-[#3b1d76]/80 leading-snug flex gap-1.5">
                        <span className="text-[#e870b6] font-bold flex-shrink-0">•</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7 — EMERGENCY */}
      <section className="bg-gradient-to-br from-[#1a0b3d] via-[#3b1d76] to-[#5b3eb5] text-white py-20 sm:py-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 [background-image:radial-gradient(circle_at_50%_50%,#e870b6_0%,transparent_60%)]" />
        <div className="relative max-w-[900px] mx-auto px-5 sm:px-8 lg:px-10 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#e870b6]/20 border border-[#e870b6]/40 text-[#ffc1e0] text-xs font-bold tracking-[0.24em] mb-6"><AlertTriangle className="w-3.5 h-3.5" />EMERGENCY CARE</span>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-6 leading-[1.02]">When Every<br/>Minute Matters.</h2>
          <p className="text-white/85 text-base sm:text-lg max-w-lg mx-auto mb-10 leading-relaxed">Same-day urgent slots during clinic hours, emergency surgical intervention and after-hours guidance for pets currently under our care. <span className="font-bold text-white">If it feels serious, please call us first.</span></p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-0 sm:divide-x sm:divide-white/15 text-left sm:text-center max-w-3xl mx-auto mb-12">
            {[
              { t: 'Urgent Consultations', s: 'Same-day slots kept aside during clinic hours.' },
              { t: 'Emergency Surgeries',  s: 'Urgent intervention after quick case assessment.' },
              { t: 'After-Hours Guidance', s: 'For pets currently under our ongoing treatment.' },
            ].map((x, i) => (
              <div key={i} className="sm:px-6">
                <h3 className="font-bold text-white mb-1.5 text-base">{x.t}</h3>
                <p className="text-sm text-white/75 leading-relaxed">{x.s}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCallClick('vet_emergency')}><Button className="w-full sm:w-auto bg-white text-[#5b3eb5] hover:bg-white/90 rounded-full px-8 py-6 font-semibold shadow-xl"><Phone className="w-5 h-5 mr-2" />Call {CONTACT.phone}</Button></a>
            <a href={wa} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick('vet_emergency')}><Button className="w-full sm:w-auto bg-[#25d366] hover:bg-[#1eb858] text-white rounded-full px-8 py-6 font-semibold shadow-xl"><MessageCircle className="w-5 h-5 mr-2" />Chat on WhatsApp</Button></a>
          </div>
        </div>
      </section>

      {/* 8 — TECHNOLOGY */}
      <section className="bg-[#fdf7fb] py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="max-w-2xl mb-12">
            <span className="inline-block text-xs font-bold tracking-[0.24em] text-[#5b3eb5] mb-4">TECHNOLOGY &amp; EQUIPMENT</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.05] mb-4">Technology That Supports<br className="hidden sm:block"/> Better Decisions.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed">Equipment we actually use, chosen for accuracy, patient comfort and faster answers.</p>
          </div>

          <div className="rounded-[28px] overflow-hidden bg-white shadow-sm border border-[#f5d3e6] grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] mb-5">
            <div className="aspect-[4/3] lg:aspect-auto overflow-hidden">
              <img src={TECH_IMAGES.surgical} alt="Operating theatre with overhead surgical lights at Ichi and Ori Pet Care, Noida" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>
            <div className="p-8 sm:p-10 lg:p-14 flex flex-col justify-center">
              <div className="text-[11px] tracking-[0.24em] font-bold text-[#e870b6] mb-3">FEATURED</div>
              <h3 className="text-2xl sm:text-3xl font-extrabold text-[#1a0b3d] mb-4 leading-tight">Surgical Suite</h3>
              <p className="text-[#3b1d76]/85 leading-relaxed text-base sm:text-lg max-w-md">A dedicated, sterile room with a full anaesthesia setup and current instrument packs. Every surgical patient is monitored closely and sent home with a clear recovery plan.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            {[
              { key: 'xray', title: 'Digital DR X-Ray', body: 'Direct-radiography imaging with instant on-screen review, used for fractures, chest and abdominal cases.' },
              { key: 'ecg',  title: 'ECG Monitor',      body: 'Cardiac tracing and vitals monitoring during anaesthesia workups and critical cases.' },
            ].map((t, i) => (
              <div key={i} className="rounded-[24px] overflow-hidden bg-white border border-[#f5d3e6] shadow-sm group">
                <div className={`aspect-[16/10] overflow-hidden ${t.key === 'xray' ? 'bg-[#fdf3fc] flex items-center justify-center p-3' : ''}`}>
                  <img
                    src={TECH_IMAGES[t.key]}
                    alt={`${t.title} at Ichi and Ori Pet Care, Noida`}
                    loading="lazy"
                    decoding="async"
                    className={
                      t.key === 'xray'
                        ? 'max-w-full max-h-full object-contain'
                        : 'w-full h-full object-cover group-hover:scale-105 transition-transform duration-700'
                    }
                  />
                </div>
                <div className="p-6 sm:p-7">
                  <h3 className="font-bold text-[#1a0b3d] text-xl mb-2">{t.title}</h3>
                  <p className="text-sm text-[#3b1d76]/80 leading-relaxed">{t.body}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { key: 'cbc',        title: 'CBC Analyzer',           body: 'In-clinic blood counts, ready within minutes.' },
              { key: 'microscope', title: 'Laboratory Microscope',  body: 'Cytology and parasitology, read at the bench.' },
              { key: 'bp',         title: 'Blood Pressure Monitor', body: 'Non-invasive BP for senior, cardiac and renal patients.' },
            ].map((t, i) => (
              <div key={i} className="rounded-[24px] overflow-hidden bg-white border border-[#f5d3e6] shadow-sm group">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={TECH_IMAGES[t.key]} alt={`${t.title} at Ichi and Ori Pet Care, Noida`} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-5 sm:p-6">
                  <h3 className="font-bold text-[#1a0b3d] text-base sm:text-lg mb-1.5">{t.title}</h3>
                  <p className="text-xs sm:text-sm text-[#3b1d76]/80 leading-relaxed">{t.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9 — TRUST STRIP */}
      <section className="bg-white border-y border-[#f5d3e6] py-10 sm:py-12">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-5 sm:gap-x-12">
            {TRUST_STRIP.map((t, i) => (
              <React.Fragment key={i}>
                <div className="flex items-center gap-2.5 text-[#1a0b3d]">
                  <t.icon className="w-4 h-4 text-[#e870b6]" />
                  <span className="text-sm font-semibold whitespace-nowrap">{t.label}</span>
                </div>
                {i < TRUST_STRIP.length - 1 && <span className="hidden sm:inline-block w-1 h-1 rounded-full bg-[#e870b6]/40" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* 9.5 — WHAT TO EXPECT (First Visit) */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">FIRST VISIT</span>
            <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-extrabold text-[#1a0b3d] leading-[1.08] mb-4">What to Expect During Your First Visit.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed">We keep every visit simple, transparent and stress-free for both pets and their families.</p>
          </div>

          <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10">
            {[
              { n: '01', Icon: CalendarDays,  t: 'Request Appointment',                  s: 'Reserve a time online, on WhatsApp or by phone. You will get a quick confirmation.' },
              { n: '02', Icon: Users,         t: 'Meet Your Veterinarian',           s: 'Dr Kaushal will greet you and your pet, and take time to understand the concern.' },
              { n: '03', Icon: Stethoscope,   t: 'Examination & Discussion',         s: 'A calm, unhurried nose-to-tail examination, with everything explained as we go.' },
              { n: '04', Icon: Microscope,    t: 'Diagnosis (if required)',          s: 'Same-visit in-house tests where useful, so we work from evidence, not guesswork.' },
              { n: '05', Icon: FileText,      t: 'Treatment Plan & Transparent Pricing', s: 'A clear plan with expected recovery and honest, upfront costs before we proceed.' },
              { n: '06', Icon: MessageCircle, t: 'Follow-up Care',                   s: 'A quick WhatsApp check-in and easy access to us if anything comes up at home.' },
            ].map((step) => (
              <li key={step.n} className="flex gap-4">
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className="w-11 h-11 rounded-full bg-[#fdf0f8] border border-[#f5d3e6] flex items-center justify-center">
                    <step.Icon className="w-5 h-5 text-[#e870b6]" />
                  </div>
                  <span className="text-[10px] tracking-[0.2em] font-bold text-[#5b3eb5] mt-2">{step.n}</span>
                </div>
                <div className="pt-1">
                  <h3 className="text-[#1a0b3d] font-bold text-base sm:text-lg mb-1.5 leading-snug">{step.t}</h3>
                  <p className="text-sm text-[#3b1d76]/80 leading-relaxed">{step.s}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* 10 — FAQ */}
      <section className="max-w-[880px] mx-auto px-5 sm:px-8 lg:px-10 py-20 sm:py-24">
        <div className="mb-12 text-center">
          <span className="inline-block text-xs font-bold tracking-[0.24em] text-[#5b3eb5] mb-4">FREQUENTLY ASKED</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.05]">Questions, Answered.</h2>
        </div>
        <Accordion type="single" collapsible defaultValue="faq-0" className="divide-y divide-[#f5d3e6] border-y border-[#f5d3e6]" data-testid="vet-faq">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="px-1">
              <AccordionTrigger data-testid={`vet-faq-q-${i}`} className="text-left text-[#1a0b3d] font-semibold text-base sm:text-lg hover:no-underline py-5 sm:py-6">{f.q}</AccordionTrigger>
              <AccordionContent className="text-[#3b1d76]/85 text-sm sm:text-base leading-relaxed pb-6">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* 10.5 — OUR STORY (teaser, links to full story on /about) */}
      <section className="bg-white pt-4 pb-20 sm:pb-24">
        <div className="max-w-[820px] mx-auto px-5 sm:px-8 lg:px-10 text-center">
          <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-5">OUR STORY</span>
          <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-extrabold text-[#1a0b3d] leading-[1.1] mb-6">We&apos;re Pet Parents First.</h2>
          <p className="text-[#3b1d76]/85 text-base sm:text-lg leading-[1.75] max-w-[680px] mx-auto mb-8">
            Ichi &amp; Ori wasn&apos;t started as just another veterinary clinic. It was built after years of loving, and losing, our own pets. Every decision begins with one simple question: <span className="text-[#1a0b3d] font-semibold italic">&ldquo;Would this be the right decision for our own pets?&rdquo;</span>
          </p>
          <Link to="/about#our-story">
            <Button className="bg-[#1a0b3d] hover:bg-[#3b1d76] text-white rounded-full px-8 py-6 text-base font-semibold shadow-lg">
              Why We Started <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* 11 — FINAL CTA */}
      <section className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 pb-16">
        <div className="rounded-[32px] bg-gradient-to-br from-[#5b3eb5] to-[#1a0b3d] text-white py-16 sm:py-20 px-6 relative overflow-hidden">
          <div className="absolute inset-0 opacity-25 [background-image:radial-gradient(circle_at_20%_30%,#e870b6_0%,transparent_45%)]" />
          <div className="relative text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4">Still have questions? We&apos;d love to help.</h2>
            <p className="text-white/85 mb-8 max-w-xl mx-auto text-base sm:text-lg">Not sure what your pet needs next? Talk to our veterinary team, and we&apos;ll guide you through the right step.</p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <Link to="/book" onClick={() => trackBookButtonClick('vet_final')}><Button className="w-full sm:w-auto bg-[#e870b6] hover:bg-[#d85ba0] rounded-full px-8 py-6 font-semibold shadow-xl"><CalendarDays className="w-5 h-5 mr-2" />Request Appointment</Button></Link>
              <a href={wa} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick('vet_final')}><Button className="w-full sm:w-auto bg-[#25d366] hover:bg-[#1eb858] rounded-full px-8 py-6 font-semibold shadow-xl"><MessageCircle className="w-5 h-5 mr-2" />Chat on WhatsApp</Button></a>
            </div>
          </div>
        </div>
      </section>

      {/* 12 — VISIT US (Google Map) */}
      <section className="bg-white pt-4 pb-28 sm:pb-36">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-8 lg:gap-14 items-center">
            <div>
              <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">VISIT US</span>
              <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-extrabold text-[#1a0b3d] leading-[1.1] mb-5">Come Meet Us in Sector 90.</h2>
              <div className="space-y-4 text-[#3b1d76] text-base sm:text-lg leading-relaxed">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#e870b6] mt-1 flex-shrink-0" />
                  <p>{CONTACT.address}</p>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-[#e870b6] mt-1 flex-shrink-0" />
                  <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCallClick('vet_visit_us')} className="hover:text-[#e870b6] transition-colors">{CONTACT.phone}</a>
                </div>
                <div className="flex items-start gap-3">
                  <CalendarDays className="w-5 h-5 text-[#e870b6] mt-1 flex-shrink-0" />
                  <p>{CONTACT.hours}</p>
                </div>
              </div>
              <a
                href={CONTACT.mapsLink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 mt-7 px-6 py-3 rounded-full bg-[#1a0b3d] hover:bg-[#3b1d76] text-white text-sm font-semibold transition-colors"
              >
                Get Directions <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="rounded-[24px] overflow-hidden border border-[#f5d3e6] shadow-lg aspect-[16/13] lg:aspect-[16/12]">
              <iframe
                title="Ichi and Ori Pet Care location on Google Maps"
                src={CONTACT.mapsEmbed}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full border-0"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VeterinaryPage;
